/**
 * "Send to a mate" — ambassador types a friend's email (+ optional note)
 * and we send the branded invite from orders@ with their referral link,
 * reply-to set to the ambassador. Backed by POST /api/affiliate/invites.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Send, Loader2, CheckCircle2, Clock, RefreshCw, UserCheck } from 'lucide-react';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { formatApiError } from '@/utils/apiError';

const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const STATUS = {
  signed_up: { icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-800', label: 'Signed up via you' },
  joined_elsewhere: { icon: UserCheck, cls: 'bg-slate-100 text-slate-700', label: 'Joined (not via your link)' },
  pending: { icon: Clock, cls: 'bg-amber-100 text-amber-800', label: 'Not yet' },
};

const InviteRow = ({ inv, onResend, busy, canSend }) => {
  const st = STATUS[inv.status] || STATUS.pending;
  const Icon = st.icon;
  return (
    <li className="py-2 flex flex-wrap items-center gap-2" data-testid={`invite-row-${inv.id}`}>
      <div className="flex-1 min-w-[160px]">
        <p className="truncate text-gray-900">{inv.to_name ? `${inv.to_name} · ` : ''}{inv.to_email}</p>
        <p className="text-[11px] text-gray-500">
          Sent {fmtDate(inv.last_sent_at || inv.created_at)}{inv.send_count > 1 ? ` · ${inv.send_count}×` : ''}
          {inv.status === 'signed_up' && inv.signed_up_at ? ` · joined ${fmtDate(inv.signed_up_at)}` : ''}
        </p>
      </div>
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${st.cls}`} data-testid={`invite-status-${inv.id}`}>
        <Icon className="w-3 h-3" /> {st.label}
      </span>
      {inv.status === 'pending' && (
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onResend(inv)} disabled={busy || !canSend} data-testid={`invite-resend-${inv.id}`}>
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Remind
        </Button>
      )}
    </li>
  );
};

export const InviteFriendCard = () => {
  const [toEmail, setToEmail] = useState('');
  const [toName, setToName] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [meta, setMeta] = useState({ invites: [], sent_today: 0, daily_cap: 25, total_sent: 0, signed_up: 0 });
  const [resending, setResending] = useState(null);

  const load = async () => {
    const res = await fetch(`${API_URL}/api/affiliate/invites?limit=25`, { credentials: 'include' });
    if (res.ok) setMeta(await res.json());
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!toEmail.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/affiliate/invites`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_email: toEmail.trim(), to_name: toName.trim() || null, note: note.trim() || null }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body) || 'Could not send');
      toast.success(`Invite sent to ${toEmail.trim()} — replies come straight to you.`);
      setToEmail(''); setToName(''); setNote('');
      setMeta((m) => ({ ...m, sent_today: body.sent_today, total_sent: m.total_sent + 1, invites: [body.invite, ...m.invites].slice(0, 25) }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const resend = async (inv) => {
    setResending(inv.id);
    try {
      const res = await fetch(`${API_URL}/api/affiliate/invites/${inv.id}/resend`, { method: 'POST', credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body) || 'Could not resend');
      toast.success(`Reminder sent to ${inv.to_email}`);
      setMeta((m) => ({ ...m, sent_today: body.sent_today, invites: m.invites.map((i) => (i.id === inv.id ? body.invite : i)) }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setResending(null);
    }
  };

  const left = Math.max(0, meta.daily_cap - meta.sent_today);
  const pending = meta.invites.filter((i) => i.status === 'pending').length;

  return (
    <Card className="p-4" data-testid="invite-friend-card">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#7A0B7A]" />
          <h2 className="text-base font-bold text-gray-900">Send to a mate</h2>
        </div>
        <span className="text-[11px] text-gray-500" data-testid="invite-quota">{left} of {meta.daily_cap} left today</span>
      </div>
      <p className="text-xs text-gray-500 mb-3">We'll email them a branded invite with your link. Replies go straight to your inbox.</p>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Input type="email" required placeholder="mate@example.com" value={toEmail} onChange={(e) => setToEmail(e.target.value)} data-testid="invite-email" />
        <Input placeholder="Their first name (optional)" value={toName} onChange={(e) => setToName(e.target.value)} maxLength={80} data-testid="invite-name" />
        <Textarea
          className="md:col-span-2 min-h-[64px]"
          placeholder="Add a personal line (optional) — e.g. 'Been using their resin for two years, best stuff out there.'"
          value={note} onChange={(e) => setNote(e.target.value)} maxLength={400} data-testid="invite-note"
        />
        <div className="md:col-span-2 flex justify-end">
          <Button type="submit" disabled={sending || left === 0} className="bg-[#7A0B7A] hover:bg-[#5e0860] text-white" data-testid="invite-send">
            {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
            {sending ? 'Sending…' : 'Send invite'}
          </Button>
        </div>
      </form>
      {meta.invites.length > 0 && (
        <div className="mt-3 pt-3 border-t" data-testid="invite-history">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Who's joined so far</p>
            <p className="text-[11px] text-gray-500" data-testid="invite-summary">
              <span className="text-emerald-700 font-semibold">{meta.signed_up} signed up</span> · {pending} still to follow up · {meta.total_sent} sent
            </p>
          </div>
          <ul className="divide-y text-sm">
            {meta.invites.map((inv) => <InviteRow key={inv.id} inv={inv} onResend={resend} busy={resending === inv.id} canSend={left > 0} />)}
          </ul>
        </div>
      )}
    </Card>
  );
};
